// src/components/Header.jsx

import { useState, useEffect, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import logo from "../assets/final_logo.png";
import { useFilters } from "../stores/useFilters";
import helpChooseImg from "../assets/help-me-choose-cars.webp"; // replace with your actual “help me choose” image
import { getBodyTypes, getMakes } from "../api/cars";
import { useQuery } from "@tanstack/react-query";
import { BODY_TYPES } from "../data/bodyTypes";

const NAV_ITEMS = [
  { key: "find", label: "Find a car" },
  { key: "how", label: "How ZORQK works" },
];

const faqs = [
  {
    question: "Do ZORQK offer new cars?",
    answer:
      "No, ZORQK only sells used cars, including nearly-new ones less than a year old and with under 5,000 miles on the clock.",
  },
  {
    question: "Do your cars come with an MOT?",
    answer:
      "Yes, all our cars come with a minimum of 6 months on the MOT and a 90-day warranty, so you can relax and enjoy your new ride.",
  },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [desktopMenu, setDesktopMenu] = useState(null); // "find" | "how" | null
  const [findCarDrawerOpen, setFindCarDrawerOpen] = useState(false);
  const [howZORQKDrawerOpen, setHowZORQKDrawerOpen] = useState(false);
  const [openIndex, setOpenIndex] = useState(null);

  const { user, token, loading, clearAuth } = useAuth();
  const navigate = useNavigate();
  const { set, reset } = useFilters();
  // ─── React Query: fetch makes+counts ─────────────────
  const {
    data: makesData = [],
    isLoading: makesLoading,
    isError: makesError,
  } = useQuery({
    queryKey: ["landing", "makes"],
    queryFn: getMakes,
    staleTime: 300_000,
  });

  // ─── Derive top 5 makes ───────────────────────────────
  const top5Makes = useMemo(() => {
    return [...makesData]
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map(({ value, count }) => ({ make: value, count }));
  }, [makesData]);

  // Fetch counts for each body-type
  const {
    data: rawBodyTypes = [], // [{ value: "SUV", count: 123 }, …]
    isLoading: bodiesLoading,
    isError: bodiesError,
  } = useQuery({
    queryKey: ["landing", "bodyTypes"],
    queryFn: getBodyTypes,
    staleTime: 300_000,
  });

  const isAdmin = user?.roles?.some((r) =>
    ["super-admin", "car-admin", "image-admin"].includes(r)
  );

  const headerRef = useRef(null);
  const panelRef = useRef(null);

  // 1) When openMenu changes, disable/enable body scroll
  // Disable scroll when either menu is open
  useEffect(() => {
    document.body.style.overflow =
      desktopMenu || findCarDrawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [desktopMenu, findCarDrawerOpen]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout failed", e);
    }
    clearAuth();
    navigate("/");
  };

  // ─── Handlers for “Find a car” submenu items ─────────────────────────────────
  const handleMakeClick = (makeValue) => {
    reset();
    set("make", makeValue);
    setOpenMenu(null);
    navigate("/used-cars");
  };

  const handleBudgetClick = (minPrice, maxPrice) => {
    reset();
    if (minPrice !== null) set("minPrice", minPrice);
    if (maxPrice !== null) set("maxPrice", maxPrice);
    setOpenMenu(null);
    navigate("/used-cars");
  };

  const handleBodyTypeClick = (bodyTypeKey) => {
    reset();
    set("bodyType", [bodyTypeKey]);
    setOpenMenu(null);
    navigate("/used-cars");
  };

  const sortedShapes = useMemo(() => {
    // build a lookup of counts
    const countMap = rawBodyTypes.reduce((m, { value, count }) => {
      m[value] = count;
      return m;
    }, {});
    // filter out zero-count, sort desc, then take only 6
    return BODY_TYPES.filter(({ key }) => (countMap[key] || 0) > 0)
      .sort((a, b) => (countMap[b.key] || 0) - (countMap[a.key] || 0))
      .slice(0, 6);
  }, [rawBodyTypes]);

  if (loading) return null;

  return (
    <header
      className="w-full bg-transparent text-white absolute top-0 z-50"
      ref={headerRef}
    >
      {/* ─── Main header bar ─────────────────────────────────────────────────────── */}
      <div className="w-full pt-6 px-2">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src={logo}
              alt="ZORQK Logo"
              className="h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav items */}
          <nav
            className={`hidden lg:flex items-center text-sm font-medium ${
              token ? "gap-10" : "gap-16"
            }`}
          >
            {NAV_ITEMS.map((item) => (
              <div key={item.key} className="relative">
                <button
                  onClick={() =>
                    setOpenMenu((prev) => (prev === item.key ? null : item.key))
                  }
                  className={`
                    inline-flex items-center
                    hover:text-accent transition text-sm
                    bg-transparent hover:bg-white py-4 px-5 rounded-t-3xl
                    ${
                      openMenu === item.key
                        ? "text-accent bg-white"
                        : "text-white"
                    }
                  `}
                >
                  <span>{item.label}</span>
                  <ChevronDownIcon
                    className={`ml-1 h-4 w-4 transform transition-transform ${
                      openMenu === item.key ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>
              </div>
            ))}

            {/* Call-to-Action Phone Number */}
            <a
              href="tel:+443337723333"
              className="inline-flex items-center bg-accent hover:bg-yellow-500 text-primary font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              📞 +44 333 772 3333
            </a>

            {isAdmin && (
              <Link
                to="/admin"
                className="inline-flex items-center hover:text-accent transition px-2"
              >
                Admin
              </Link>
            )}

            {/* Sign‐in / Profile / Logout */}
            {/* {!token ? (
              <Link
                to="/signin"
                className="inline-flex items-center hover:text-accent transition text-white hover:bg-white p-4 rounded-t-3xl"
              >
                <UserCircleIcon className="h-6 w-6" />
                <span className="ml-1">Sign In</span>
              </Link>
            ) : (
              <div className="flex items-center gap-8 px-4">
                <Link
                  to="/profile"
                  className="inline-flex items-center hover:text-accent"
                >
                  <UserCircleIcon className="h-6 w-6" />
                  <span className="ml-1">{user.fullName.split(" ")[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center hover:text-accent transition"
                >
                  <ArrowLeftOnRectangleIcon className="h-6 w-6" />
                  <span className="ml-1">Logout</span>
                </button>
              </div>
            )} */}

            {token && (
              <div className="flex items-center gap-12 px-4">
                <Link
                  to="/profile"
                  className="inline-flex items-center hover:text-accent"
                >
                  <UserCircleIcon className="h-6 w-6" />
                  <span className="ml-1">{user.fullName.split(" ")[0]}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center hover:text-accent transition"
                >
                  <ArrowLeftOnRectangleIcon className="h-6 w-6" />
                  <span className="ml-1">Logout</span>
                </button>
              </div>
            )}

            {/* <Link
              to="/"
              className=" text-sm bg-accent inline-flex items-center transition text-white px-4 py-2 rounded-full hover:opacity-90"
            >
              Download on iOS
            </Link>

            <Link
              to="/"
              className="text-sm bg-accent inline-flex items-center transition text-white px-4 py-2 rounded-full hover:opacity-90"
            >
              Download on Android
            </Link> */}
          </nav>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            className="lg:hidden text-white focus:outline-none"
          >
            {mobileOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* ─── Mobile collapsible nav ────────────────────────────────────────────── */}
        {mobileOpen && (
          <>
            <aside
              className={`
        fixed inset-y-0 right-0
        w-3/5 sm:w-1/2 rounded-tl-3xl
        bg-white shadow-2xl z-50
        transform transition-transform duration-300
        ${mobileOpen ? "translate-x-0" : "translate-x-full"}
      `}
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-bold text-accent">Menu</h2>
                <button onClick={() => setMobileOpen(false)}>
                  <XMarkIcon className="h-6 w-6 text-primary" />
                </button>
              </div>
              {/* Drawer body */}
              <nav className="px-4 py-6 space-y-4">
                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setFindCarDrawerOpen(true);
                  }}
                  className="w-full text-left text-lg font-medium text-primary hover:text-accent"
                >
                  Find a car
                </button>

                <button
                  onClick={() => {
                    setMobileOpen(false);
                    setHowZORQKDrawerOpen(true);
                  }}
                  className="w-full text-left text-lg font-medium text-primary hover:text-accent"
                >
                  How ZORQK works
                </button>

                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileOpen(false)}
                    className="block text-lg text-primary font-medium hover:text-accent"
                  >
                    Admin
                  </Link>
                )}

                {!token ? (
                  <Link
                    to="/signin"
                    onClick={() => setMobileOpen(false)}
                    className="block text-lg text-primary font-medium hover:text-accent"
                  >
                    Sign In
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="block text-lg text-primary font-medium hover:text-accent"
                    >
                      {user.fullName.split(" ")[0]}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileOpen(false);
                      }}
                      className="w-full text-left text-lg font-medium text-primary hover:text-accent"
                    >
                      Logout
                    </button>
                  </>
                )}
              </nav>
            </aside>

            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setMobileOpen(false)}
            />
          </>
        )}
      </div>

      {/*─── BACKDROP*/}
      {openMenu && (
        <div
          className="fixed inset-x-0 top-20 bottom-0 backdrop-blur-xl z-30"
          onClick={() => setOpenMenu(null)}
        />
      )}

      {/* ─── Desktop : Find a car side-drawer ───────────────── */}
      {openMenu === "find" && (
        <div
          ref={panelRef}
          className="absolute inset-x-0 top-full bg-white shadow-2xl z-40 md:mx-10 md:py-10 rounded-2xl"
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1.5fr_1.5fr] gap-8">
              {/* ─── Column 1: Popular makes ──────────────────────────────── */}
              <div>
                <h3 className="text-xl font-semibold text-brandAccent mb-4">
                  Popular makes
                </h3>
                <ul className="space-y-4 text-primary font-medium">
                  {top5Makes.map(({ make, count }) => (
                    <li key={make}>
                      <button
                        onClick={() => handleMakeClick(make)}
                        className="hover:text-accent transition"
                      >
                        {make} ({count})
                      </button>
                    </li>
                  ))}
                  <li className="mt-2">
                    <button
                      onClick={() => {
                        reset();
                        setOpenMenu(null);
                        navigate("/used-cars");
                      }}
                      className="inline-flex items-center text-accent hover:text-accent transition"
                    >
                      View all cars&nbsp;
                      <ChevronUpIcon className="h-4 w-4 rotate-90" />
                    </button>
                  </li>
                </ul>
              </div>

              {/* ─── Column 2: Shop by budget + Resources and guides ────────── */}
              <div>
                <h3 className="text-xl font-semibold text-brandAccent mb-4">
                  Shop by budget
                </h3>
                <ul className="space-y-4 text-primary font-medium mb-6">
                  <li>
                    <button
                      onClick={() => handleBudgetClick(null, 7500)}
                      className="hover:text-accent transition"
                    >
                      Cars under £7,500
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleBudgetClick(null, 10000)}
                      className="hover:text-accent transition"
                    >
                      Cars under £10,000
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleBudgetClick(null, 15000)}
                      className="hover:text-accent transition"
                    >
                      Cars under £15,000
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleBudgetClick(null, 20000)}
                      className="hover:text-accent transition"
                    >
                      Cars under £20,000
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleBudgetClick(20000, null)}
                      className="hover:text-accent transition"
                    >
                      Cars over £20,000
                    </button>
                  </li>
                </ul>

                {/* <h3 className="text-xl font-semibold text-brandAccent mb-4">
                  Resources and guides
                </h3>
                <ul className="space-y-4 text-primary font-medium">
                  <li>
                    <Link
                      to="#"
                      // to="/car-reviews"
                      onClick={() => setOpenMenu(null)}
                      className="hover:text-accent transition"
                    >
                      Car reviews
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      // to="/guides"
                      onClick={() => setOpenMenu(null)}
                      className="hover:text-accent transition"
                    >
                      Guides
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      // to="/electric-cars"
                      onClick={() => setOpenMenu(null)}
                      className="hover:text-accent transition"
                    >
                      Electric cars
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="#"
                      // to="/news"
                      onClick={() => setOpenMenu(null)}
                      className="hover:text-accent transition"
                    >
                      News
                    </Link>
                  </li>
                </ul> */}
              </div>

              {/* ─── Column 3: Shop by type ─────────────────────────────────── */}
              <div>
                <h3 className="text-xl font-semibold text-brandAccent mb-4">
                  Shop by shape
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {sortedShapes.map((body) => (
                    <button
                      key={body.key}
                      onClick={() => handleBodyTypeClick(body.key)}
                      className="
                        flex flex-col items-center
                        px-2 rounded
                        hover:shadow-lg transition
                      "
                    >
                      <span className="text-sm pb-2 text-secondary">
                        {body.label}
                      </span>
                      <img
                        src={body.img}
                        alt={body.label}
                        className="w-60 h-16 object-cover mb-2"
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* ─── Column 4: Help me choose card ────────────────────────────── */}
              <div>
                <div className="bg-primary rounded-xl shadow p-6 flex flex-col justify-between">
                  {/* 1) Top text (left-aligned) */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      Help me choose
                    </h3>
                    <p className="text-gray-300 mb-4">
                      Contact us to and go from 1000s of cars to tailored top
                      picks
                    </p>
                  </div>

                  {/* 2) Centered image */}
                  <div className="flex justify-center mb-6">
                    <Link
                      to="/which-car-should-i-buy"
                      onClick={() => setOpenMenu(null)}
                    >
                      <img
                        src={helpChooseImg}
                        alt="Help me choose"
                        className="w-full h-36 object-cover"
                      />
                    </Link>
                  </div>

                  {/* 3) Bottom link (left-aligned by default) */}
                  <div>
                    <Link
                      to="/which-car-should-i-buy"
                      onClick={() => setOpenMenu(null)}
                      className="text-accent font-semibold hover:underline text-sm"
                    >
                      Help me choose&nbsp;→
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile : Find a car side-drawer */}
      <aside
        className={`
          fixed inset-y-0 right-0 w-full sm:w-2/3 bg-white shadow-2xl z-50
          transform transition-transform duration-300 rounded-tl-2xl
          ${findCarDrawerOpen ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-accent">Find a car</h2>
          <button onClick={() => setFindCarDrawerOpen(false)}>
            <XMarkIcon className="h-6 w-6 text-primary" />
          </button>
        </div>
        {/* Drawer content */}
        <div className="overflow-y-auto h-full px-4 py-6 space-y-6">
          {/* Section 1: Popular makes */}
          <details className="border-b pb-4" open>
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">Popular makes</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>
            <ul className="mt-3 space-y-2">
              {top5Makes.map(({ make, count }) => (
                <li key={make}>
                  <button
                    onClick={() => {
                      handleMakeClick(make);
                      setFindCarDrawerOpen(false);
                    }}
                    className="w-full text-left text-primary hover:text-accent"
                  >
                    {make} ({count})
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => {
                    reset();
                    navigate("/used-cars");
                    setFindCarDrawerOpen(false);
                  }}
                  className="text-accent hover:underline"
                >
                  View all cars →
                </button>
              </li>
            </ul>
          </details>

          {/* Section 2: Shop by budget */}
          <details className="border-b pb-4">
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">Shop by budget</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>
            <ul className="mt-3 space-y-2">
              {[7500, 10000, 15000, 20000].map((amt) => (
                <li key={amt}>
                  <button
                    onClick={() => {
                      handleBudgetClick(null, amt);
                      setFindCarDrawerOpen(false);
                    }}
                    className="w-full text-left text-primary hover:text-accent"
                  >
                    Cars under £{amt.toLocaleString()}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() => {
                    handleBudgetClick(20000, null);
                    setFindCarDrawerOpen(false);
                  }}
                  className="w-full text-left text-primary hover:text-accent"
                >
                  Cars over £20,000
                </button>
              </li>
            </ul>
          </details>

          {/* Section 3: Shop by shape */}
          <details className="border-b pb-4">
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">Shop by shape</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>
            <div className="mt-3 grid grid-cols-2 gap-4">
              {sortedShapes.map(({ key, label, img }) => (
                <button
                  key={key}
                  onClick={() => {
                    handleBodyTypeClick(key);
                    setFindCarDrawerOpen(false);
                  }}
                  className="flex flex-col items-center p-2 hover:shadow-lg"
                >
                  <span className="text-sm text-primary">{label}</span>
                  <img
                    src={img}
                    alt={label}
                    className="w-24 h-12 object-cover mb-1 rounded"
                  />
                </button>
              ))}
            </div>
          </details>

          {/* Section 4: Help me choose */}
          <details className="border-b pb-4">
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">Help me choose</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>
            <div className="mt-3 space-y-4">
              <p className="text-primary mb-4">
                Contact us to and go from 1000s of cars to tailored top picks
              </p>

              <div className="flex justify-center mb-6">
                <Link
                  to="/which-car-should-i-buy"
                  onClick={() => setOpenMenu(null)}
                >
                  <img
                    src={helpChooseImg}
                    alt="Help me choose"
                    className="w-full h-36 object-cover"
                  />
                </Link>
              </div>
              <Link
                to="/which-car-should-i-buy"
                onClick={() => setFindCarDrawerOpen(false)}
                className="block bg-primary text-white py-2 rounded text-center"
              >
                Get started →
              </Link>
            </div>

            {/* <div>
              <div className="bg-primary rounded-xl shadow p-6 flex flex-col justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Help me choose
                  </h3>
                  <p className="text-gray-300 mb-4">
                    Contact us to and go from 1000s of cars to tailored top
                    picks
                  </p>
                </div>

                <div className="flex justify-center mb-6">
                  <Link
                    to="/which-car-should-i-buy"
                    onClick={() => setOpenMenu(null)}
                  >
                    <img
                      src={helpChooseImg}
                      alt="Help me choose"
                      className="w-full h-36 object-cover"
                    />
                  </Link>
                </div>

                <div>
                  <Link
                    to="/which-car-should-i-buy"
                    onClick={() => setOpenMenu(null)}
                    className="text-accent font-semibold hover:underline text-sm"
                  >
                    Help me choose&nbsp;→
                  </Link>
                </div>
              </div>
            </div> */}
          </details>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {findCarDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setFindCarDrawerOpen(false)}
        />
      )}

      {/* ─── Desktop : Find a car side-drawer ───────────────── */}
      {openMenu === "how" && (
        <div
          ref={panelRef}
          className={`absolute 
      top-full 
      right-0
      md:${token ? "-right-40" : "-right-0"}
      -translate-x-0
      w-full 
      max-w-[800px]
      bg-white 
      shadow-2xl 
      z-40 
      px-4 sm:px-6 md:px-16 py-0 md:py-10
      rounded-2xl`}
        >
          {/* Two-column grid for “How ZORQK works” */}
          <div className="grid grid-cols-1 md:grid-cols-2 py-5">
            {/* ─── Left Column: List of “How ZORQK works” items ───────── */}
            <div className="mb-5 md:mb-0">
              <h3 className="text-xl font-semibold text-brandAccent mb-4">
                How ZORQK works
              </h3>
              <ul className="flex flex-col sm:flex space-y-1 md:space-y-4 text-primary font-medium">
                <li>
                  <Link
                    to="/which-car-should-i-buy"
                    onClick={() => setOpenMenu(null)}
                    className="hover:text-accent transition"
                  >
                    Buying a car online
                  </Link>
                </li>
                <li>
                  <Link
                    to="/90-day-warranty"
                    onClick={() => setOpenMenu(null)}
                    className="hover:text-accent transition"
                  >
                    90-day warranty
                  </Link>
                </li>
                <li>
                  <Link
                    to="/quality-standards"
                    onClick={() => setOpenMenu(null)}
                    className="hover:text-accent transition"
                  >
                    Quality standards
                  </Link>
                </li>
                <li>
                  <Link
                    to="/delivery"
                    onClick={() => setOpenMenu(null)}
                    className="hover:text-accent transition"
                  >
                    Home delivery
                  </Link>
                </li>
              </ul>
            </div>

            {/* ─── Right Column: FAQ box ──────────────────────────────────── */}
            <div className="relative">
              {/* FAQ Card */}
              <div className="bg-primary rounded-xl p-6 shadow">
                <h3 className="text-xl font-semibold text-accent mb-4">FAQs</h3>
                <ul className="space-y-4 text-white">
                  {faqs.map((faq, i) => (
                    <li key={i}>
                      <button
                        onClick={() => setOpenIndex(i)}
                        className="w-full text-left hover:text-accent transition"
                      >
                        {faq.question}
                      </button>
                      <hr className="border-gray-200 my-2" />
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <Link
                    to="/contact-us"
                    onClick={() => setOpenMenu(null)}
                    className="inline-flex items-center text-accent hover:underline"
                  >
                    Go to help and support&nbsp;→
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Faqs Modal */}
      {openIndex !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white text-primary rounded-lg max-w-md w-full p-6 relative">
            <h4 className="text-lg font-medium mb-4">
              {faqs[openIndex].question}
            </h4>
            <p className="mb-6">{faqs[openIndex].answer}</p>
            <button
              onClick={() => setOpenIndex(null)}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
              aria-label="Close FAQ"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* ─── Mobile: How ZORQK works drawer ─────────────────────── */}
      <aside
        className={`
    fixed inset-y-0 right-0
    w-full sm:w-2/3
    bg-white shadow-2xl z-50
    transform transition-transform duration-300
    rounded-tl-2xl
    ${howZORQKDrawerOpen ? "translate-x-0" : "translate-x-full"}
  `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-accent">How ZORQK works</h2>
          <button onClick={() => setHowZORQKDrawerOpen(false)}>
            <XMarkIcon className="h-6 w-6 text-primary" />
          </button>
        </div>
        {/* Body */}
        <div className="overflow-y-auto h-full px-4 py-6 space-y-6">
          {/* Overview section */}
          <details className="border-b pb-4" open>
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">Overview</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>
            <ul className="mt-3 space-y-2 text-primary">
              <li>
                <Link
                  to="/which-car-should-i-buy"
                  onClick={() => setHowZORQKDrawerOpen(false)}
                  className="block hover:text-accent"
                >
                  Buying a car online
                </Link>
              </li>
              <li>
                <Link
                  to="/90-day-warranty"
                  onClick={() => setHowZORQKDrawerOpen(false)}
                  className="block hover:text-accent"
                >
                  90-day warranty
                </Link>
              </li>
              <li>
                <Link
                  to="/quality-standards"
                  onClick={() => setHowZORQKDrawerOpen(false)}
                  className="block hover:text-accent"
                >
                  Quality standards
                </Link>
              </li>
              <li>
                <Link
                  to="/delivery"
                  onClick={() => setHowZORQKDrawerOpen(false)}
                  className="block hover:text-accent"
                >
                  Home delivery
                </Link>
              </li>
            </ul>
          </details>

          {/* FAQ section */}
          <details className="border-b pb-4">
            <summary className="flex justify-between items-center cursor-pointer">
              <span className="font-semibold text-primary">FAQs</span>
              <div className="flex items-center">
                <ChevronDownIcon className="h-5 w-5 details-icon-closed" />
                <ChevronUpIcon className="h-5 w-5 details-icon-open hidden" />
              </div>
            </summary>

            <ul className="mt-3 space-y-4 text-primary">
              {faqs.map((faq, i) => (
                <li key={i}>
                  <button
                    onClick={() => {
                      // setHowZORQKDrawerOpen(false);
                      setOpenIndex(i);
                    }}
                    className="w-full text-left hover:text-accent transition"
                  >
                    {faq.question}
                  </button>
                </li>
              ))}

              <li>
                <Link
                  to="/contact-us"
                  onClick={() => setHowZORQKDrawerOpen(false)}
                  className="block text-accent hover:underline"
                >
                  Go to help & support&nbsp;→
                </Link>
              </li>
            </ul>
          </details>

          {/* Modal Overlay */}
          {openIndex !== null && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="bg-white text-primary rounded-lg max-w-sm w-full p-6 relative mx-4">
                <h4 className="text-lg  font-medium mb-4">
                  {faqs[openIndex].question}
                </h4>
                <p className="mb-6">{faqs[openIndex].answer}</p>
                <button
                  onClick={() => setOpenIndex(null)}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                  aria-label="Close FAQ"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Backdrop for How ZORQK drawer */}
      {howZORQKDrawerOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setHowZORQKDrawerOpen(false)}
        />
      )}
    </header>
  );
}
